import { useCallback, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { ApiError } from '@/api/client';
import { useSession } from '@/auth/SessionContext';
import { ScreenBackButton } from '@/components/ScreenBackButton';

type AttributeRow = { key: string; value: string };

type CalendarResource = {
  id: string;
  name: string;
  description: string | null;
  capacity: number;
  isActive: boolean;
  customAttributes: Record<string, unknown> | null;
  resourceType?: { name?: string } | null;
};

type ResourceTypeRow = {
  id: string;
  name: string;
  category: string;
  checkInTime: string | null;
  checkOutTime: string | null;
};

type StayHoursDraft = { checkIn: string; checkOut: string };

const TIME_HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

function toHhMm(value: string | null | undefined): string {
  const match = /^(\d{2}):(\d{2})/.exec((value ?? '').trim());
  return match ? `${match[1]}:${match[2]}` : '';
}

function attributeRows(raw: unknown): AttributeRow[] {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return [];
  return Object.entries(raw as Record<string, unknown>).map(([key, value]) => ({
    key,
    value: value == null ? '' : String(value),
  }));
}

function attributesPayload(rows: AttributeRow[]): Record<string, string> | null {
  const out: Record<string, string> = {};
  for (const row of rows) {
    const key = row.key.trim();
    if (!key) throw new Error('Las claves de las propiedades no pueden estar vacías.');
    if (Object.prototype.hasOwnProperty.call(out, key)) {
      throw new Error(`La propiedad «${key}» está repetida.`);
    }
    out[key] = row.value;
  }
  return Object.keys(out).length > 0 ? out : null;
}

export function AgentResourcesPage() {
  const { agentId = '' } = useParams();
  const location = useLocation();
  const agentName =
    (location.state as { agentName?: string } | null)?.agentName?.trim() || 'Agente';
  const { get, put, can } = useSession();
  const canWrite = can('pwa.calendar.write');
  const [resources, setResources] = useState<CalendarResource[]>([]);
  const [stayTypes, setStayTypes] = useState<ResourceTypeRow[]>([]);
  const [stayHours, setStayHours] = useState<Record<string, StayHoursDraft>>({});
  const [savingHoursId, setSavingHoursId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [capacity, setCapacity] = useState('1');
  const [isActive, setIsActive] = useState(true);
  const [attributes, setAttributes] = useState<AttributeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [rows, types] = await Promise.all([
        get<CalendarResource[]>(`/api/agents/${agentId}/calendar/resources`),
        get<ResourceTypeRow[]>(`/api/agents/${agentId}/calendar/resource-types`),
      ]);
      setResources([...rows].sort((a, b) => a.name.localeCompare(b.name, 'es')));
      const hospitality = types.filter((type) => type.category === 'hoteleria');
      setStayTypes(hospitality);
      setStayHours(
        Object.fromEntries(
          hospitality.map((type) => [
            type.id,
            { checkIn: toHhMm(type.checkInTime), checkOut: toHhMm(type.checkOutTime) },
          ]),
        ),
      );
    } catch (e) {
      setResources([]);
      setStayTypes([]);
      setStayHours({});
      setErr(e instanceof ApiError ? e.message : 'No se pudieron cargar los recursos');
    } finally {
      setLoading(false);
    }
  }, [agentId, get]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      void load();
    }, 0);
    return () => clearTimeout(t);
  }, [load]);

  const selected = resources.find((resource) => resource.id === selectedId) ?? null;

  function openResource(resource: CalendarResource) {
    setSelectedId(resource.id);
    setName(resource.name);
    setDescription(resource.description ?? '');
    setCapacity(String(resource.capacity));
    setIsActive(resource.isActive);
    setAttributes(attributeRows(resource.customAttributes));
    setErr(null);
    setSaved(null);
  }

  function updateStayHours(typeId: string, patch: Partial<StayHoursDraft>) {
    setStayHours((current) => {
      const prev = current[typeId] ?? { checkIn: '', checkOut: '' };
      return { ...current, [typeId]: { ...prev, ...patch } };
    });
  }

  async function saveStayHours(type: ResourceTypeRow) {
    if (!canWrite) return;
    const draft = stayHours[type.id] ?? { checkIn: '', checkOut: '' };
    const checkInTime = toHhMm(draft.checkIn);
    const checkOutTime = toHhMm(draft.checkOut);
    if (!TIME_HHMM.test(checkInTime) || !TIME_HHMM.test(checkOutTime)) {
      setErr('Indicá check-in y check-out en formato HH:mm.');
      setSaved(null);
      return;
    }
    setSavingHoursId(type.id);
    setErr(null);
    setSaved(null);
    try {
      const updated = await put<ResourceTypeRow>(
        `/api/agents/${agentId}/calendar/resource-types/${type.id}`,
        { checkInTime, checkOutTime },
      );
      setStayTypes((current) => current.map((row) => (row.id === updated.id ? { ...row, ...updated } : row)));
      setStayHours((current) => ({
        ...current,
        [type.id]: { checkIn: toHhMm(updated.checkInTime), checkOut: toHhMm(updated.checkOutTime) },
      }));
      setSaved('Horario general guardado.');
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'No se pudo guardar el horario');
    } finally {
      setSavingHoursId(null);
    }
  }

  async function save() {
    if (!selected || !canWrite) return;
    const capacityNumber = Number(capacity);
    if (!name.trim()) {
      setErr('El nombre es obligatorio.');
      return;
    }
    if (!Number.isInteger(capacityNumber) || capacityNumber < 1) {
      setErr('La capacidad tiene que ser un número entero de al menos 1.');
      return;
    }
    let customAttributes: Record<string, string> | null;
    try {
      customAttributes = attributesPayload(attributes);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Revisá las propiedades');
      return;
    }
    setSaving(true);
    setErr(null);
    setSaved(null);
    try {
      const updated = await put<CalendarResource>(
        `/api/agents/${agentId}/calendar/resources/${selected.id}`,
        {
          name: name.trim(),
          description: description.trim() || null,
          capacity: capacityNumber,
          isActive,
          customAttributes,
        },
      );
      setResources((current) =>
        current
          .map((resource) => (resource.id === updated.id ? { ...resource, ...updated } : resource))
          .sort((a, b) => a.name.localeCompare(b.name, 'es')),
      );
      setSaved('Propiedades guardadas.');
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'No se pudieron guardar las propiedades');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div className="pwa-screen-head">
        {selected ?
          <button type="button" className="pwa-back-btn" aria-label="Volver a los recursos" onClick={() => setSelectedId(null)}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" d="M15 6l-6 6 6 6" />
            </svg>
          </button>
        : <ScreenBackButton to="/" ariaLabel="Volver al inicio" />}
        <h1 className="pwa-screen-head__title">{selected ? selected.name : agentName}</h1>
      </div>

      {err ? <p className="pwa-err">{err}</p> : null}
      {saved ? <p className="pwa-muted">{saved}</p> : null}

      {selected ?
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <p className="pwa-muted" style={{ marginTop: 0 }}>
            {selected.resourceType?.name ? `${selected.resourceType.name} · ` : ''}
            Propiedades del recurso
          </p>
          <label className="pwa-muted" style={{ display: 'block', fontSize: '0.8rem' }}>
            Nombre
            <input className="pwa-field" value={name} disabled={!canWrite} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="pwa-muted" style={{ display: 'block', fontSize: '0.8rem', marginTop: '0.75rem' }}>
            Capacidad
            <input
              className="pwa-field"
              inputMode="numeric"
              value={capacity}
              disabled={!canWrite}
              onChange={(e) => setCapacity(e.target.value)}
            />
          </label>
          <label className="pwa-muted" style={{ display: 'block', fontSize: '0.8rem', marginTop: '0.75rem' }}>
            Descripción
            <textarea
              className="pwa-field"
              rows={3}
              value={description}
              disabled={!canWrite}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: '0.85rem' }}>
            <input
              type="checkbox"
              checked={isActive}
              disabled={!canWrite}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            Activo
          </label>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '1.25rem' }}>
            <h2 className="pwa-muted" style={{ fontSize: '0.95rem', fontWeight: 600, margin: 0 }}>
              Propiedades
            </h2>
            {canWrite ?
              <button
                type="button"
                className="pwa-btn pwa-btn--secondary pwa-btn--sm"
                onClick={() => setAttributes((rows) => [...rows, { key: '', value: '' }])}
              >
                Agregar
              </button>
            : null}
          </div>
          {attributes.length === 0 ?
            <p className="pwa-muted">Sin propiedades.</p>
          : attributes.map((row, index) => (
              <div key={index} style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <input
                  className="pwa-field"
                  style={{ marginTop: 0, flex: 1, width: 'auto', minWidth: 0 }}
                  placeholder="Clave"
                  aria-label={`Clave ${index + 1}`}
                  value={row.key}
                  disabled={!canWrite}
                  onChange={(e) =>
                    setAttributes((rows) => rows.map((item, i) => (i === index ? { ...item, key: e.target.value } : item)))
                  }
                />
                <input
                  className="pwa-field"
                  style={{ marginTop: 0, flex: 1, width: 'auto', minWidth: 0 }}
                  placeholder="Valor"
                  aria-label={`Valor ${index + 1}`}
                  value={row.value}
                  disabled={!canWrite}
                  onChange={(e) =>
                    setAttributes((rows) =>
                      rows.map((item, i) => (i === index ? { ...item, value: e.target.value } : item)),
                    )
                  }
                />
                {canWrite ?
                  <button
                    type="button"
                    className="pwa-btn pwa-btn--secondary pwa-btn--sm"
                    aria-label={`Quitar propiedad ${index + 1}`}
                    onClick={() => setAttributes((rows) => rows.filter((_, i) => i !== index))}
                  >
                    Quitar
                  </button>
                : null}
              </div>
            ))}

          {canWrite ?
            <button type="submit" className="pwa-btn pwa-btn--primary" style={{ marginTop: '1.25rem' }} disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          : <p className="pwa-muted">Podés ver las propiedades, pero no editarlas.</p>}
        </form>
      : loading ?
        <p className="pwa-muted">Cargando recursos…</p>
      : <>
          {stayTypes.length > 0 ?
            <section aria-label="Horario general" style={{ marginBottom: '1.25rem' }}>
              <h2 className="pwa-muted" style={{ fontSize: '0.95rem', fontWeight: 600, margin: '0 0 0.5rem' }}>
                Horario general
              </h2>
              {stayTypes.map((type) => {
                const draft = stayHours[type.id] ?? {
                  checkIn: toHhMm(type.checkInTime),
                  checkOut: toHhMm(type.checkOutTime),
                };
                return (
                  <form
                    key={type.id}
                    className="pwa-card"
                    style={{ marginBottom: '0.5rem' }}
                    onSubmit={(event) => {
                      event.preventDefault();
                      void saveStayHours(type);
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>{type.name}</div>
                    <p className="pwa-muted" style={{ margin: '0.25rem 0 0' }}>
                      Check-in y check-out para todos los días. El domingo se informa con la regla del agente.
                    </p>
                    {canWrite ?
                      <>
                        <label className="pwa-muted" style={{ display: 'block', fontSize: '0.8rem', marginTop: '0.75rem' }}>
                          Check-in
                          <input
                            className="pwa-field"
                            type="time"
                            value={draft.checkIn}
                            onChange={(e) => updateStayHours(type.id, { checkIn: e.target.value })}
                          />
                        </label>
                        <label className="pwa-muted" style={{ display: 'block', fontSize: '0.8rem', marginTop: '0.75rem' }}>
                          Check-out
                          <input
                            className="pwa-field"
                            type="time"
                            value={draft.checkOut}
                            onChange={(e) => updateStayHours(type.id, { checkOut: e.target.value })}
                          />
                        </label>
                        <button
                          type="submit"
                          className="pwa-btn pwa-btn--primary pwa-btn--sm"
                          style={{ marginTop: '0.85rem' }}
                          disabled={savingHoursId === type.id}
                        >
                          {savingHoursId === type.id ? 'Guardando…' : 'Guardar horario'}
                        </button>
                      </>
                    : <p style={{ margin: '0.5rem 0 0' }}>
                        Check-in {draft.checkIn || 'sin definir'} · Check-out {draft.checkOut || 'sin definir'}
                      </p>}
                  </form>
                );
              })}
            </section>
          : null}
          {resources.length === 0 ?
            <p className="pwa-muted">Este agente no tiene recursos de calendario.</p>
          : <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {resources.map((resource) => (
                <li key={resource.id} style={{ marginBottom: '0.5rem' }}>
                  <button
                    type="button"
                    className="pwa-card pwa-card--press"
                    style={{ width: '100%', textAlign: 'left', color: 'inherit' }}
                    onClick={() => openResource(resource)}
                  >
                    <span style={{ fontWeight: 600 }}>{resource.name}</span>
                    {!resource.isActive ? <span className="pwa-muted"> · inactivo</span> : null}
                    <div className="pwa-muted" style={{ fontSize: '0.8rem', marginTop: 4 }}>
                      {resource.resourceType?.name ? `${resource.resourceType.name} · ` : ''}
                      {Object.keys(resource.customAttributes ?? {}).length} propiedades
                    </div>
                  </button>
                </li>
              ))}
            </ul>}
        </>}
    </div>
  );
}
